import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useP } from '../../i18n/useP';
import { updateSalaryMonths } from '../../api/hr';
import '../TenantSettingsPage.css';

/* Paramètres généraux RH — réglages transverses au module People.
   Onglet « Autres paramètres » du hub People › Données. Écrit via /api/hr
   (périmètre `people`), pas via les réglages du client. Mois de versement des salaires
   au-delà du 12e, en deux blocs indépendants : contrats à 13 salaires (13e en
   1 ou 2 fois) et contrats à 14 salaires (mois propres du 13e et du 14e). */
export default function HrGeneralSettingsPage({ activeTenant, addToast, onTenantUpdated }) {
  const { t } = useTranslation();
  const p = useP();
  const [saving, setSaving] = useState(false);

  async function patch(fields, okMsg) {
    setSaving(true);
    try {
      const res = await updateSalaryMonths(activeTenant.id, fields);
      if (res.data) {
        onTenantUpdated && onTenantUpdated(res.data);
        addToast && addToast(okMsg);
        return true;
      }
      addToast && addToast(res.error || t('common:errors.generic'), 'error');
    } catch (err) {
      addToast && addToast(t('common:errors.withMessage', { message: err.message }), 'error');
    } finally {
      setSaving(false);
    }
    return false;
  }

  if (!activeTenant) return <p className="settings-empty">{p('Aucune entreprise sélectionnée.')}</p>;

  function monthRow({ id, field, label, hint, fallback, okPrefix }) {
    return (
      <div className="settings-row">
        <label className="settings-lbl" htmlFor={id}>
          {label}
          {hint && <span className="settings-hint">{hint}</span>}
        </label>
        <select
          id={id}
          className="settings-input"
          value={String(activeTenant[field] || fallback)}
          onChange={e => {
            const n = Number(e.target.value);
            patch(
              { [field]: n },
              `${p(okPrefix)} ${p('versé en')} ${t(`common:monthsFull.${n}`)}.`,
            );
          }}
          disabled={saving}
        >
          {Array.from({ length: 12 }, (_, i) => (
            <option key={i + 1} value={i + 1}>{t(`common:monthsFull.${i + 1}`)}</option>
          ))}
        </select>
      </div>
    );
  }

  // 13e versé en 1 ou 2 fois : month_2 > 0 ⇒ 2 versements (50/50).
  const split13 = Number(activeTenant.thirteenth_salary_month_2 || 0) > 0;

  return (
    <>
      {/* Bloc 13 salaires — un 13e, versé en 1 ou 2 fois */}
      <section className="settings-card">
        <div className="settings-card-title">{p('Contrats à 13 salaires')}</div>
        <div className="settings-row">
          <label className="settings-lbl" htmlFor="hr-m13-count">
            {p('13e salaire — nombre de versements')}
            <span className="settings-hint">{p('le 13e peut être payé en une fois ou scindé')}</span>
          </label>
          <select
            id="hr-m13-count"
            className="settings-input"
            value={split13 ? '2' : '1'}
            onChange={e => {
              if (e.target.value === '2') {
                // Scinder : split classique Juin (1er) + Décembre (2e). On conserve
                // un découpage déjà personnalisé (2e mois > 0) plutôt que l'écraser.
                const had2 = Number(activeTenant.thirteenth_salary_month_2 || 0) > 0;
                patch(
                  had2 ? {} : { thirteenth_salary_month: 6, thirteenth_salary_month_2: 12 },
                  p('13e versé en 2 fois.'),
                );
              } else {
                // 1 versement : pas de 2e mois, le 13e retombe sur décembre.
                patch({ thirteenth_salary_month: 12, thirteenth_salary_month_2: 0 }, p('13e versé en 1 fois.'));
              }
            }}
            disabled={saving}
          >
            <option value="1">{p('1 versement')}</option>
            <option value="2">{p('2 versements (scindé 50/50)')}</option>
          </select>
        </div>
        {monthRow({
          id: 'hr-m13',
          field: 'thirteenth_salary_month',
          label: split13 ? p('13e salaire — 1er versement') : p('13e salaire — mois de versement'),
          hint: split13 ? null : p('positionné sur ce mois dans les vues People (pas étalé)'),
          fallback: 12,
          okPrefix: '13e salaire',
        })}
        {split13 && monthRow({
          id: 'hr-m13b',
          field: 'thirteenth_salary_month_2',
          label: p('13e salaire — 2e versement'),
          hint: null,
          fallback: 12,
          okPrefix: '2e versement du 13e',
        })}
      </section>

      {/* Bloc 14 salaires — deux versements pleins, mois propres (13e + 14e) */}
      <section className="settings-card">
        <div className="settings-card-title">{p('Contrats à 14 salaires')}</div>
        {monthRow({
          id: 'hr-m14-13',
          field: 'fourteenth_salary_month_13',
          label: p('13e salaire — mois de versement'),
          hint: null,
          fallback: 6,
          okPrefix: '13e salaire (14 salaires)',
        })}
        {monthRow({
          id: 'hr-m14-14',
          field: 'fourteenth_salary_month',
          label: p('14e salaire — mois de versement'),
          hint: null,
          fallback: 12,
          okPrefix: '14e salaire',
        })}
      </section>
    </>
  );
}
